import { useState, useCallback } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { GradientText, MagneticButton, RippleButton } from "@/components/effects";
import { Link } from "wouter";

const heroSlides = [
  {
    badge: "Business messaging for India",
    heading: ["Bulk SMS Service Provider", "in India.", "Clear setup. Practical delivery."],
    gradientFrom: "#FF6B2C",
    gradientVia: "#FF9F1C",
    gradientTo: "#FFD700",
    description: "Plan promotional, transactional and OTP communication with a bulk SMS service provider built for Indian business messaging workflows.",
    cta: { text: "APP Login", href: "https://app.bharatbulksms.com/" },
    secondaryCta: { text: "View Pricing", href: "/pricing" },
    image: "/manus-storage/homepage-sms-bharat_8bc1070b.png",
    webp: {
      mobile: "/assets/homepage-sms-640_c2db61bb.webp",
      standard: "/assets/homepage-sms-960_49ba3e4b.webp",
      desktop: "/assets/homepage-sms-1280_ce67fe5d.webp",
    },
    imageAlt: "Bulk SMS campaign dashboard interface",
    imageWidth: 1280,
    imageHeight: 960,
    badges: ["Campaign controls", "DLT guidance", "API options"],
    floatingBadge: { label: "Message types", value: "SMS" },
    orbColors: ["from-saffron/10", "from-blue-600/10"],
  },
  {
    badge: "WhatsApp Business messaging",
    heading: ["WhatsApp Business", "API for India.", "Rich Media. Chatbots. Scale."],
    gradientFrom: "#25D366",
    gradientVia: "#128C7E",
    gradientTo: "#075E54",
    description: "Use templates, rich media, catalog messages and automation options to support relevant business conversations on WhatsApp.",
    cta: { text: "Get WhatsApp API Access", href: "/whatsapp-business-api" },
    secondaryCta: { text: "See Demo", href: "/contact" },
    image: "/manus-storage/homepage-whatsapp-bharat_f1d22dbd.png",
    webp: {
      mobile: "/assets/homepage-whatsapp-640_9b907309.webp",
      standard: "/assets/homepage-whatsapp-960_57692d26.webp",
      desktop: "/assets/homepage-whatsapp-1280_e86bf6be.webp",
    },
    imageAlt: "WhatsApp Business API messaging interface",
    imageWidth: 1280,
    imageHeight: 720,
    badges: ["Rich media", "Template support", "Chatbot ready"],
    floatingBadge: { label: "Channel", value: "WhatsApp" },
    orbColors: ["from-green-500/10", "from-emerald-600/10"],
  },
  {
    badge: "Full-Stack Digital Marketing Agency",
    heading: ["Digital Marketing", "for considered growth.", "SEO. Ads. Social. Websites."],
    gradientFrom: "#8B5CF6",
    gradientVia: "#A855F7",
    gradientTo: "#D946EF",
    description: "Google Ads, SEO, social media marketing, and website design — all under one roof. Data-driven strategies built for the Indian market.",
    cta: { text: "Discuss Marketing Needs", href: "/contact" },
    secondaryCta: { text: "Our Services", href: "/services" },
    image: "/manus-storage/homepage-marketing-bharat_46f83cbf.png",
    webp: {
      mobile: "/assets/homepage-marketing-640_2002d792.webp",
      standard: "/assets/homepage-marketing-960_661749a8.webp",
      desktop: "/assets/homepage-marketing-1280_481fef74.webp",
    },
    imageAlt: "Digital marketing campaign dashboard interface",
    imageWidth: 1280,
    imageHeight: 720,
    badges: ["Search campaigns", "SEO planning", "Website design"],
    floatingBadge: { label: "Services", value: "4 areas" },
    orbColors: ["from-purple-500/10", "from-pink-600/10"],
  },
];

export default function HomeHeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative overflow-hidden bg-[#0a0e1a] min-h-[90vh] sm:min-h-[95vh] flex items-center">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-[0.03]" style={{
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,107,44,0.3) 40px, rgba(255,107,44,0.3) 41px)`
      }} />
      <div className={`absolute top-[-15%] right-[-5%] w-[500px] h-[500px] rounded-full bg-gradient-to-br ${slide.orbColors[0]} to-transparent blur-[100px] transition-all duration-1000`} />
      <div className={`absolute bottom-[-15%] left-[-5%] w-[400px] h-[400px] rounded-full bg-gradient-to-tr ${slide.orbColors[1]} to-transparent blur-[100px] transition-all duration-1000`} />
      <div className="absolute top-20 right-[15%] w-20 h-20 border border-white/[0.06] rotate-45 hidden lg:block" />
      <div className="absolute bottom-32 left-[10%] w-12 h-12 border border-saffron/10 rotate-12 hidden lg:block" />

      <div className="container relative z-10 py-14 sm:py-20 lg:py-0">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div key={currentSlide} className="animate-fade-in-up" style={{ animationDuration: "500ms", animationFillMode: "both" }}>
              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] backdrop-blur-sm rounded-md px-4 py-2 mb-6 sm:mb-8 border border-white/[0.08] font-mono">
                <span className="w-2 h-2 bg-green-400 rounded-sm animate-pulse" />
                <span className="text-xs sm:text-sm text-white/70 tracking-wide uppercase">{slide.badge}</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-extrabold text-white leading-[1.1] mb-5 sm:mb-7 tracking-tight">
                <GradientText from={slide.gradientFrom} via={slide.gradientVia} to={slide.gradientTo} className="font-extrabold">{slide.heading[0]}</GradientText>
                <span className="block mt-1">{slide.heading[1]}</span>
                <span className="block mt-2 text-white/60 text-2xl sm:text-3xl lg:text-4xl font-semibold">{slide.heading[2]}</span>
              </h1>

              <div className="flex gap-4 mb-7 sm:mb-9">
                <div className="w-[3px] bg-gradient-to-b from-saffron to-transparent rounded-full shrink-0 hidden sm:block" />
                <p className="text-base sm:text-lg text-white/60 max-w-lg leading-relaxed">{slide.description}</p>
              </div>

              <div className="flex flex-wrap gap-3 sm:gap-4 mb-7 sm:mb-10">
                <MagneticButton className="group">
                  <RippleButton href={slide.cta.href} className="inline-flex items-center bg-gradient-to-r from-saffron to-[#FF9F1C] hover:from-[#FF9F1C] hover:to-saffron text-white font-bold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg shadow-lg shadow-saffron/20 transition-all duration-300 hover:shadow-xl hover:shadow-saffron/30 hover:-translate-y-0.5">
                    {slide.cta.text} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </RippleButton>
                </MagneticButton>
                <MagneticButton>
                  <Link href={slide.secondaryCta.href} className="inline-flex items-center border border-white/20 text-white hover:bg-white/[0.06] hover:border-white/30 font-medium text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg transition-all duration-300 backdrop-blur-sm">
                    {slide.secondaryCta.text}
                  </Link>
                </MagneticButton>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {slide.badges.map((badge, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-xs sm:text-sm text-white/70 font-mono">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 mt-4 lg:mt-0">
            <div key={`img-${currentSlide}`} className="animate-fade-in-up" style={{ animationDuration: "600ms", animationDelay: "100ms", animationFillMode: "both" }}>
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-saffron/15 via-transparent to-blue-500/10 rounded-2xl blur-2xl" />
                <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/[0.08] rounded-xl p-3 shadow-2xl shadow-black/30">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                    <span className="ml-3 text-[10px] text-white/30 font-mono">dashboard.bharatbulksms.com</span>
                  </div>
                  <picture className="block aspect-[4/3]">
                    <source media="(max-width: 767px)" srcSet={slide.webp.mobile} type="image/webp" />
                    <source media="(min-width: 768px)" srcSet={`${slide.webp.standard} 1x, ${slide.webp.desktop} 2x`} type="image/webp" />
                    <img
                      src={slide.image}
                      alt={slide.imageAlt}
                      width={slide.imageWidth}
                      height={slide.imageHeight}
                      sizes="(min-width: 1024px) 40vw, 100vw"
                      loading={currentSlide === 0 ? "eager" : "lazy"}
                      fetchPriority={currentSlide === 0 ? "high" : "low"}
                      decoding="async"
                      className="w-full h-full rounded-lg object-contain"
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-saffron w-8' : 'bg-white/30 hover:bg-white/50'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
